import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import { selectUser } from "app/slice/selectors";
import { CongratulationsDialog } from "../../FixedPhone/components/congratulations-dialog";
import { CheckCoverageDialog } from "./check-coverage-dialog";
import { CoverageDialog } from "app/pages/Customer/NetworkPlanPurchase/NetworkPlan/components/coverage-dialog";
import { clearFixedPhonePurchaseFlow } from "app/pages/Customer/FixedPhone/Checkout/utils/fixed-phone-storage";
import {
  ACTIVATION_ENTRY_SEARCH_PARAM,
  ActivationEntryType,
} from "app/pages/Customer/Activation/activation-entry-types";
import { createAbsoluteUrl } from "utils/constants";

import { Separator } from "app/components/ui/separator";
import { Button } from "app/components/ui/button";
import { Badge } from "app/components/ui/badge";

const activateOptions = [
  {
    icon: "/assets/preloaded-sim.svg",
    altText: "Sim Card",
    title: "Preloaded SIM card activation (new accounts only)",
    description: "Pick this option for SIM cards preloaded with a service plan",
    isComingSoon: false,
  },
  {
    icon: "/assets/phone-office.svg",
    altText: "Phone Office",
    title: "Activate fixed phone base*",
    description: "Choose this option if you purchased a base from a reseller",
    isComingSoon: false,
  },
];

export const Activate = () => {
  const user = useSelector(selectUser);
  const [selectedOptionTitle, setSelectedOptionTitle] = useState<string | null>(
    null,
  );
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isSimZipCoverageOpen, setIsSimZipCoverageOpen] = useState(false);
  const [isCongratsOpen, setIsCongratsOpen] = useState(false);
  const [restoredData, setRestoredData] = useState<any>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => {
    clearFixedPhonePurchaseFlow();
  }, []);

  useEffect(() => {
    const entryType = searchParams.get(ACTIVATION_ENTRY_SEARCH_PARAM);
    const nextParams = new URLSearchParams(searchParams);
    nextParams.delete(ACTIVATION_ENTRY_SEARCH_PARAM);

    if (entryType === ActivationEntryType.PreloadedSim) {
      setSelectedOptionTitle(activateOptions[0].title);
      setIsSimZipCoverageOpen(true);
      if (nextParams.toString()) {
        setSearchParams(nextParams, { replace: true });
      } else {
        setSearchParams({}, { replace: true });
      }
    } else if (entryType === ActivationEntryType.FixedPhoneBase) {
      setSelectedOptionTitle(activateOptions[1].title);
      setIsDialogOpen(true);
      if (nextParams.toString()) {
        setSearchParams(nextParams, { replace: true });
      } else {
        setSearchParams({}, { replace: true });
      }
    }

    // Check for restore_congrats via location state
    const state = location.state as { congratulationsData?: any };
    if (state?.congratulationsData) {
      setRestoredData(state.congratulationsData);
      setIsCongratsOpen(true);
      // We don't need to open the main dialog or set title since we show the congrats dialog directly

      // Clear state to prevent loop
      navigate(location.pathname + location.search, {
        replace: true,
        state: {},
      });
    }
  }, [searchParams, setSearchParams, location, navigate]);

  const handleContinue = () => {
    if (selectedOptionTitle === activateOptions[0].title) {
      setIsSimZipCoverageOpen(true);
    } else if (selectedOptionTitle === activateOptions[1].title) {
      setIsDialogOpen(true);
    }
  };

  return (
    <React.Fragment>
      <section className="flex justify-center items-center w-full h-[calc(100dvh-8rem)] sm:h-[calc(100vh-9.625rem)] lg:h-[calc(100vh-11.375rem)] min-h-[45rem] sm:min-h-[30rem] max-h-[60rem] px-5 sm:px-6 md:px-7 lg:px-9 xl:px-12 pb-32 sm:pb-9">
        <div className="bg-white w-full sm:max-w-[34.125rem] p-4 pt-3 sm:p-5 sm:pt-[0.8125rem] lg:p-6 lg:pt-[0.875rem] border border-[#E5E7EB] rounded-xl md:rounded-2xl">
          <div className="flex flex-col-reverse sm:flex-row justify-between gap-x-4 gap-y-2 items-start mb-3.5 sm:mb-4 lg:mb-[1.125rem]">
            <div className="space-y-0.5 sm:space-y-1">
              <h4 className="text-[1.375rem] sm:text-[1.4375rem] font-bold leading-6 text-black lg:text-2xl sm:leading-[1.625rem] lg:leading-7 font-mobile">
                Activate your service
              </h4>
              <p className="text-[0.8125rem] sm:text-[0.84375rem] font-medium leading-[1.125rem] sm:leading-5 text-black opacity-50 md:leading-[1.375rem] md:text-sm">
                Make a selection to proceed
              </p>
            </div>
            <div className="flex items-center gap-2 sm:gap-2.5 md:gap-3 mt-1 sm:mt-1.5">
              <Button
                type="button"
                variant="link"
                className="p-0 h-auto font-bold text-[#FE4D01] hover:text-[#E44501] text-[0.71875rem] sm:text-xs uppercase underline underline-offset-2 hover:decoration-[#E44501]"
                onClick={() =>
                  navigate(user ? "/my-account/dashboard" : "/login")
                }
              >
                Sign In
              </Button>
              <Separator
                orientation="vertical"
                className="h-auto self-stretch bg-transparent border-l border-[#FE4D0180]"
              />
              <Button
                type="button"
                variant="link"
                className="p-0 h-auto font-bold text-[#FE4D01] hover:text-[#E44501] text-[0.71875rem] sm:text-xs uppercase underline underline-offset-2 hover:decoration-[#E44501]"
                onClick={() => navigate("/network-plan")}
              >
                Plan Builder
              </Button>
            </div>
          </div>
          <ul className="space-y-3.5 sm:space-y-4 lg:space-y-5 mb-3.5 sm:mb-4 lg:mb-5">
            {activateOptions.map((option, index) => (
              <li
                key={index}
                className={`relative flex flex-col sm:flex-row sm:items-center gap-2.5 p-3 sm:p-2.5 border rounded-2xl md:rounded-[1.125rem] ${
                  option.isComingSoon
                    ? "bg-[#F2F2F280] cursor-auto"
                    : selectedOptionTitle === option.title
                    ? "bg-[#FE4D0114] border-[#FE4D0166] transition-all duration-300 cursor-pointer"
                    : "bg-[#F9F9F9] border-transparent hover:bg-[#FE4D010D] cursor-pointer"
                }`}
                onClick={() => {
                  if (option.isComingSoon) return;
                  setSelectedOptionTitle(option.title);
                }}
              >
                {option.isComingSoon && (
                  <Badge
                    variant="default"
                    className="absolute -top-2.5 sm:-top-[0.6875rem] lg:-top-3 right-3.5 sm:right-4 bg-[#FEEDE6] hover:bg-[#FEEDE6] h-5 sm:h-[1.375rem] lg:h-6 px-2 py-0.5 border-none rounded-full shadow-none z-10"
                  >
                    <span className="inline-block uppercase font-dmsans text-[0.5625rem] sm:text-[0.625rem] font-semibold leading-[1.125rem] sm:leading-5 text-[#FE4D01] whitespace-nowrap">
                      Coming Soon
                    </span>
                  </Badge>
                )}
                <div
                  className={`shrink-0 flex-grow-0 flex justify-center items-center ${
                    selectedOptionTitle === option.title
                      ? "bg-white transition-all duration-300 "
                      : "bg-[#EAEAEA]"
                  } size-[3.75rem] sm:size-16 lg:size-[4.25rem] rounded-2xl md:rounded-[1.125rem]`}
                >
                  <img
                    src={option.icon}
                    alt={option.altText}
                    className="size-[2.125rem] sm:size-8 lg:size-[1.875rem]"
                  />
                </div>
                <div className="text-left">
                  <h4
                    className={`font-mobile text-black text-[1.0625rem] sm:text-[1.03125rem] lg:text-base font-bold leading-[1.125rem] sm:leading-[1.1875rem] lg:leading-5 my-1 ${
                      selectedOptionTitle === option.title
                        ? "transition-all duration-300"
                        : ""
                    }`}
                  >
                    {option.title.split(/(\(.*?\))|(\*)/).map((part, i) => {
                      if (!part) return null;
                      if (part.startsWith("(") && part.endsWith(")")) {
                        return (
                          <span
                            key={i}
                            className="inline-block text-[#333333] text-[0.9375rem] sm:text-[0.90625rem] lg:text-sm font-normal leading-[1.125rem] sm:leading-[1.0625rem] lg:leading-4"
                          >
                            {part}
                          </span>
                        );
                      }
                      if (part === "*") {
                        return (
                          <span key={i} className="inline-block font-black">
                            {part}
                          </span>
                        );
                      }
                      return part;
                    })}
                  </h4>
                  <p className="text-black/50 text-[0.84375rem] sm:text-[0.78125rem] lg:text-xs font-medium leading-[1.0625rem] sm:leading-[1.125rem]">
                    {option.description}
                  </p>
                </div>
              </li>
            ))}
          </ul>
          <Button
            type="button"
            variant="primary"
            className="h-10 sm:h-11"
            onClick={handleContinue}
            disabled={!selectedOptionTitle}
          >
            Continue
          </Button>
          <p className="text-left text-xs sm:text-[0.78125rem] lg:text-[0.8125rem] text-[#7C7C7C] font-normal leading-4 sm:leading-[1.125rem] lg:leading-5 mt-3 sm:mt-3.5 lg:mt-4">
            {/* <span className="text-black/60 font-bold">Existing Customers:</span>{" "} */}
            <span className="inline-block font-black">*</span>To activate a
            device on an existing account, please contact{" "}
            <a
              href={createAbsoluteUrl("/support-center")}
              className="font-extrabold underline text-black hover:text-[#FE4D01] transition-colors cursor-pointer"
            >
              customer support.
            </a>
          </p>
        </div>
      </section>
      <CoverageDialog
        open={isSimZipCoverageOpen}
        onOpenChange={(isOpen) => {
          setIsSimZipCoverageOpen(isOpen);
          if (!isOpen) {
            setSelectedOptionTitle(null);
          }
        }}
        activationZipFlow
        preloadedSimZipFlow={selectedOptionTitle === activateOptions[0].title}
        onActivationCoverageSuccess={() => {
          navigate("/activation/preloaded-sim/device-info");
          setSelectedOptionTitle(null);
        }}
      />
      <CheckCoverageDialog
        showDialog={isDialogOpen}
        setShowDialog={(isOpen) => {
          setIsDialogOpen(isOpen);
          if (!isOpen) {
            setSelectedOptionTitle(null);
          }
        }}
        onContinue={() => {
          navigate("/activation/fixed-phone/activation-code");
          setSelectedOptionTitle(null);
        }}
      />
      <CongratulationsDialog
        showDialog={isCongratsOpen}
        setShowDialog={setIsCongratsOpen}
        apiResponse={restoredData?.apiResponse}
        fullAddress={restoredData?.fullAddress}
        isActivation={true}
        onContinue={() => {
          navigate("/activation/fixed-phone/activation-code");
          setIsCongratsOpen(false);
          setRestoredData(null);
        }}
      />
    </React.Fragment>
  );
};
